import { useState, useEffect, useImperativeHandle, useRef, useCallback } from 'react';
import { calculateSkidkaOrMarzha, calculatePriceFromSkidkaOrMarzha } from './calculationsDiscountOrMarzha';
import { useAnalogs } from './useAnalogs';
import { useDelivery } from './useDelivery';
import { GLOBAL_CONSTANTS } from '../../constants';
import { restoreAppScrollPosition } from '../../utils/pageScroll';

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

const GAZ_DELIVERY_CATEGORY_NAMES = new Set([
  'бордюр',
  'бордюр дорожный',
  'брусчатка',
  'брусчатка и тротуарная плитка',
  'плитка тротуарная',
  'газонная решетка',
  'лоток водоотводный жби',
  'лоток водоотводный тротуарный',
]);

const isGazDeliveryCategory = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized.includes('газобетон') || GAZ_DELIVERY_CATEGORY_NAMES.has(normalized);
};

// Функция для определения категории товара
const getCategoryForMaterial = (material) => {
  if (!material.calculator) return 'os'; // по умолчанию Общестрой

  const categoryMap = new Map(
    Object.entries(GLOBAL_CONSTANTS.CATEGORY_NAMES)
      .map(([key, value]) => [value.trim().toLowerCase(), key])
  );

  return categoryMap.get(material.calculator.trim().toLowerCase()) || 'os';
};

const isGazMaterial = (material) => {
  const calculator = String(material?.calculatorCategory || material?.calculator || '').toLowerCase();
  const category = String(material?.category || '').toLowerCase();
  return isGazDeliveryCategory(calculator) || isGazDeliveryCategory(category);
};
const isGazOffer = (materials = []) => Array.isArray(materials) && materials.some(isGazMaterial);

// Проблема округления цены до целого рубля перед умножением на количество актуальна только
// для кирпича (малая цена за штуку, партии в тысячи штук) — не для газобетона/бордюра/брусчатки,
// которые тоже живут в calculator "Газобетон и кирпич" через isGazMaterial. Поэтому здесь — узкая
// проверка именно по подкатегории material.category, а не по всей "gaz"-группе.
const isBrickMaterial = (material) => String(material?.category || '').toLowerCase().includes('кирпич');

const getPricePrecision = (material) => (isBrickMaterial(material) ? 2 : 0);

const roundPriceForMaterial = (value, material) => {
  const precision = getPricePrecision(material);
  const factor = 10 ** precision;
  return Math.round((parseFloat(value) || 0) * factor) / factor;
};

// Итоговая сумма по строке: для обычных материалов округляем вверх до целого рубля (как раньше),
// для газобетона/кирпича — до сотых, чтобы не терять копейки при больших количествах.
const roundSumForMaterial = (value, material) => {
  const precision = getPricePrecision(material);
  if (precision > 0) {
    const factor = 10 ** precision;
    return Math.round((Number(value) || 0) * factor) / factor;
  }
  return Math.ceil(Number(value) || 0);
};

const parsePositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

export const useOfferState = (selectedOffer, addressAndCoordinates, dealId, ref, options, clientName, analogsData, deliveryMethod, setDeliveryMethod, setSelectedOffer) => {
  const [originalMaterials, setOriginalMaterials] = useState([]);
  const [originalDelivery, setOriginalDelivery] = useState(null);
  const [localPrices, setLocalPrices] = useState([]);
  const [localDeliveryPrice, setLocalDeliveryPrice] = useState(0);
  const [localSkidka, setLocalSkidka] = useState([]);
  const [localSkidkaD, setLocalSkidkaD] = useState(null);
  const [localGlobalSkidka, setLocalGlobalSkidka] = useState('');
  const [per, setPer] = useState({ id: 1, text: 'Скидка' });
  const [analogs, setAnalogs] = useState([]);
  const [activeOffers, setActiveOffers] = useState({
    original: null,
    analog1: null,
    analog2: null,
    analog3: null,
    analog4: null,
    analog5: null,
    analog6: null,
  });
  const [responseText, setResponseText] = useState('');
  const [analogDeliveries, setAnalogDeliveries] = useState({
    analog1: null,
    analog2: null,
    analog3: null,
    analog4: null,
    analog5: null,
    analog6: null,
  });
  const [buttonStates, setButtonStates] = useState({
    1: false, // НЕ БРОНИРУЕМ
    2: false, // analog1
    3: false, // analog2
    4: false, // analog3
    5: false, // analog4
    6: false, // analog5
    7: false, // analog6
    8: false, // Нет, предложенный аналог хуже
  });
  const [notification, setNotification] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, text: "", x: 0, y: 0, targetElement: null });
  const [isLoadingAnalogs, setIsLoadingAnalogs] = useState(false);
  const [additionalMaterials, setAdditionalMaterials] = useState([]);

  const localRef = useRef(null);

  // Состояние для хранения cache.densityRanges
  const [densityRanges, setDensityRanges] = useState([]);
  const [izolifeSettings, setIzolifeSettings] = useState({ settings: [], deliveryCostByRegion: {}, truckPalletsByProduct: {} });
  const cache = { densityRanges, izolifeSettings }; // Формируем объект cache для передачи в useAnalogs

  // Запрос данных densityRanges при монтировании компонента
  useEffect(() => {
    const fetchDensityRanges = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/density-ranges`);
        const result = await response.json();
        if (result.success) {
          setDensityRanges(result.data);
          console.log('✅ Данные densityRanges успешно загружены:', result.data);
        } else {
          console.error('❌ Ошибка при загрузке densityRanges:', result.error);
          setDensityRanges([]);
        }
      } catch (error) {
        console.error('❌ Ошибка при запросе densityRanges:', error);
        setDensityRanges([]);
      }
    };

    const fetchIzolifeSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_PATH_TO_BACKEND}/api/izolife-settings`);
        const result = await response.json();
        if (result.success) {
          setIzolifeSettings({
            settings: result.settings,
            deliveryCostByRegion: result.deliveryCostByRegion,
            truckPalletsByProduct: result.truckPalletsByProduct,
          });
          console.log('✅ Данные izolifeSettings успешно загружены');
        } else {
          console.error('❌ Ошибка при загрузке izolifeSettings:', result.error);
        }
      } catch (error) {
        console.error('❌ Ошибка при запросе izolifeSettings:', error);
      }
    };

    fetchDensityRanges();
    fetchIzolifeSettings();
  }, []);

  const { getAnalogs } = useAnalogs(selectedOffer, options, analogsData, 'cash', cache);
  const { recalculateDelivery } = useDelivery(addressAndCoordinates);
  const offerRegion = selectedOffer?.region || addressAndCoordinates?.region || 'СПБ';

  const restoreWindowScrollPosition = useCallback((scrollY) => {
    restoreAppScrollPosition(scrollY);
  }, []);

  const getDeliveryRecalculationInput = (materials, fallbackName = '') => {
    if (!isGazOffer(materials)) {
      return fallbackName;
    }

    return {
      name: deliveryMethod?.gaz || fallbackName,
      unloadMethod: deliveryMethod?.gazUnloadMethod || null,
    };
  };

  useImperativeHandle(ref, () => localRef.current);

  useEffect(() => {
    if (selectedOffer) {
      const newMaterials = selectedOffer.materials || [];
      const newDelivery = selectedOffer.delivery || {
        name: '',
        numberOfTrips: 0,
        costPerTrip: 0,
        totalCost: 0,
      };

      setAdditionalMaterials(newMaterials.filter((material) => material?.isFromInsulationFriends));
      setOriginalMaterials(newMaterials);
      setOriginalDelivery(newDelivery);
      setLocalPrices(newMaterials.map((mat) => roundPriceForMaterial(mat.pricePerOne, mat)));
      setLocalDeliveryPrice(Math.round(parseFloat(newDelivery.costPerTrip) || 0));
      setLocalSkidka(new Array(newMaterials.length).fill(''));
      setLocalSkidkaD('');
      setLocalGlobalSkidka('');
      setActiveOffers((prev) => ({
        ...prev,
        original: { materials: newMaterials, delivery: newDelivery, type: 'original' },
      }));

      if (newMaterials.length > 0 && options && analogs.length === 0) {
        setIsLoadingAnalogs(true);
        console.log('Processing materials:', newMaterials);
        console.log('Options:', options);
        const calculateAnalogs = async () => {
          const materialAnalogs = await getAnalogs(newMaterials[0].name, newMaterials[0].quantity);
          console.log('materialAnalogs:', materialAnalogs);
          // Фильтруем аналоги с volumeBenefit < 0 (экономия)
          const filteredAnalogs = materialAnalogs.filter(analog => analog && analog.volumeBenefit < 0);
          setAnalogs(filteredAnalogs);

          const calculateAnalogDeliveries = async () => {
            try {
              const newAnalogDeliveries = { ...analogDeliveries };
              if (newDelivery.name === 'Самовывоз') {
                // При самовывозе устанавливаем доставку для всех аналогов в null
                for (let i = 1; i <= 6 && i < filteredAnalogs.length; i++) {
                  newAnalogDeliveries[`analog${i}`] = null;
                }
              } else {
                // Рассчитываем доставку только для не-самовывоза
                for (let i = 1; i <= 6 && i < filteredAnalogs.length; i++) {
                  if (!newAnalogDeliveries[`analog${i}`]) {
                    const analog = filteredAnalogs[i];
                    const newMaterial = {
                      ...newMaterials[0],
                      name: analog.value,
                      pricePerOne: Math.ceil(analog.pricePerPackage),
                      sum: Math.ceil(analog.pricePerPackage * analog.requiredPackages),
                      manufacturer: analog.details.manufacturer,
                      volumePerOne: analog.volumePerOne,
                      massPerOne: analog.massPerOne,
                      unit: analog.details.unit || newMaterials[0].unit,
                      quantity: analog.requiredPackages,
                    };
                    const delivery = await recalculateDelivery(
                      [newMaterial],
                      getDeliveryRecalculationInput([newMaterial], newDelivery.name),
                      selectedOffer?.region || addressAndCoordinates?.region || 'СПБ'
                    );
                    if (delivery && delivery.totalCost > 0) { // Проверяем, что доставка валидна
                      newAnalogDeliveries[`analog${i}`] = delivery;
                    } else {
                      newAnalogDeliveries[`analog${i}`] = null; // Устанавливаем null, если доставка невалидна
                    }
                  }
                }
              }
              setAnalogDeliveries(newAnalogDeliveries);
            } finally {
              setIsLoadingAnalogs(false);
            }
          };

          calculateAnalogDeliveries();
        };

        calculateAnalogs();
      }
    }
  }, [selectedOffer?.production, selectedOffer?.materials, options, analogs.length]);

  useEffect(() => {
    const originalOfferMaterials = activeOffers?.original?.materials || [];
    if (!originalOfferMaterials.length || !isGazOffer(originalOfferMaterials)) return;

    setLocalPrices((prev) => {
      const hasInvalid = prev.some((value) => !Number.isFinite(Number(value)));
      if (prev.length === originalOfferMaterials.length && !hasInvalid) {
        return prev;
      }

      return originalOfferMaterials.map((mat, index) => {
        const prevValue = Number(prev[index]);
        if (Number.isFinite(prevValue)) {
          return roundPriceForMaterial(prevValue, mat);
        }
        return roundPriceForMaterial(mat.pricePerOne, mat);
      });
    });

    setLocalSkidka((prev) => {
      if (prev.length === originalOfferMaterials.length) return prev;
      if (prev.length > originalOfferMaterials.length) {
        return prev.slice(0, originalOfferMaterials.length);
      }
      return [...prev, ...new Array(originalOfferMaterials.length - prev.length).fill('')];
    });
  }, [activeOffers?.original?.materials]);

  const handleMaterialPriceChange = (index, newPrice, offerType) => {
    const offer = activeOffers[offerType];
    const material = offerType === 'original' ? originalMaterials[index] : offer.materials[index];
    // Берем исходную цену из originalMaterials для правильного расчета скидки
    const originalPrice = offerType === 'original'
      ? roundPriceForMaterial(originalMaterials[index]?.pricePerOne, material)
      : roundPriceForMaterial(offer.materials[index].pricePerOne, material);
    const parsedPrice = roundPriceForMaterial(newPrice, material);
    const newSkidka = calculateSkidkaOrMarzha(parsedPrice, originalPrice, per.id === 2);

    setActiveOffers((prev) => ({
      ...prev,
      [offerType]: {
        ...offer,
        materials: offer.materials.map((mat, j) =>
          j === index
            ? { ...mat, pricePerOne: parsedPrice, sum: roundSumForMaterial(parsedPrice * mat.quantity, material) }
            : mat
        ),
      },
    }));
    setLocalPrices((prev) => (offerType === 'original' ? prev.map((p, i) => (i === index ? parsedPrice : p)) : prev));
    setLocalSkidka((prev) => (offerType === 'original' ? prev.map((s, i) => (i === index ? newSkidka : s)) : prev));
  };

  const handleSkidkaChange = (index, skidka, offerType) => {
    const offer = activeOffers[offerType];
    const material = offerType === 'original' ? originalMaterials[index] : offer.materials[index];
    // Берем исходную цену из originalMaterials, а не из текущего состояния
    const originalPrice = offerType === 'original'
      ? roundPriceForMaterial(originalMaterials[index]?.pricePerOne, material)
      : roundPriceForMaterial(offer.materials[index].pricePerOne, material);
    const parsedSkidka = skidka === '' ? '' : Math.round(parseFloat(skidka) || 0);
    const newPrice = calculatePriceFromSkidkaOrMarzha(originalPrice, parsedSkidka, per.id === 2, getPricePrecision(material));

    setActiveOffers((prev) => ({
      ...prev,
      [offerType]: {
        ...offer,
        materials: offer.materials.map((mat, j) =>
          j === index
            ? { ...mat, pricePerOne: newPrice, sum: roundSumForMaterial(newPrice * mat.quantity, material) }
            : mat
        ),
      },
    }));
    setLocalSkidka((prev) => (offerType === 'original' ? prev.map((s, i) => (i === index ? parsedSkidka : s)) : prev));
    setLocalPrices((prev) => (offerType === 'original' ? prev.map((p, i) => (i === index ? newPrice : p)) : prev));
  };

  const handleGlobalSkidkaChange = (skidka, offerType) => {
    if (offerType !== 'original') return;

    const parsedGlobalSkidka = skidka === '' ? '' : Math.round(parseFloat(skidka) || 0);
    setLocalGlobalSkidka(parsedGlobalSkidka);

    const offer = activeOffers[offerType];

    const updatedMaterials = offer.materials.map((material, index) => {
      // Берем исходную цену из originalMaterials
      const originalPrice = roundPriceForMaterial(originalMaterials[index]?.pricePerOne, material);
      const newPrice = calculatePriceFromSkidkaOrMarzha(originalPrice, parsedGlobalSkidka, per.id === 2, getPricePrecision(material));
      return {
        ...material,
        pricePerOne: newPrice,
        sum: roundSumForMaterial(newPrice * material.quantity, material),
      };
    });

    let updatedDelivery = offer.delivery;
    if (offer.delivery && offer.delivery.name !== 'Самовывоз') {
      // Берем исходную цену доставки из originalDelivery
      const originalDeliveryPrice = Math.round(parseFloat(originalDelivery?.costPerTrip) || 0);
      const newDeliveryPrice = calculatePriceFromSkidkaOrMarzha(originalDeliveryPrice, parsedGlobalSkidka, per.id === 2);
      updatedDelivery = {
        ...offer.delivery,
        costPerTrip: newDeliveryPrice,
        totalCost: Math.ceil(newDeliveryPrice * offer.delivery.numberOfTrips),
      };
    }

    setActiveOffers((prev) => ({
      ...prev,
      [offerType]: {
        ...offer,
        materials: updatedMaterials,
        delivery: updatedDelivery,
      },
    }));

    setLocalPrices(updatedMaterials.map((mat) => roundPriceForMaterial(mat.pricePerOne, mat)));
    setLocalSkidka(updatedMaterials.map(() => parsedGlobalSkidka));
    if (updatedDelivery) {
      setLocalDeliveryPrice(Math.round(parseFloat(updatedDelivery.costPerTrip) || 0));
      setLocalSkidkaD(parsedGlobalSkidka);
    }
  };

  const skidkaOrMarzha = () => {
    setPer((prev) => {
      const newId = prev.id === 1 ? 2 : 1;
      const newText = prev.id === 1 ? 'Маржа' : 'Скидка';

      const updateOffer = (offer) => {
        if (!offer) return null;
        const newLocalPrices = offer.materials.map((mat, index) => {
          const originalPrice = roundPriceForMaterial(mat.pricePerOne, mat);
          const skidka = offer.type === 'original' ? localSkidka[index] : '';
          const parsedSkidka = skidka === '' ? '' : Math.round(parseFloat(skidka) || 0);
          return calculatePriceFromSkidkaOrMarzha(originalPrice, parsedSkidka, newId === 2, getPricePrecision(mat));
        });

        return {
          ...offer,
          materials: offer.materials.map((mat, index) => ({
            ...mat,
            pricePerOne: newLocalPrices[index],
            sum: roundSumForMaterial(newLocalPrices[index] * mat.quantity, mat),
          })),
        };
      };

      const newOffers = {
        original: updateOffer(activeOffers.original),
        analog1: updateOffer(activeOffers.analog1),
        analog2: updateOffer(activeOffers.analog2),
        analog3: updateOffer(activeOffers.analog3),
        analog4: updateOffer(activeOffers.analog4),
        analog5: updateOffer(activeOffers.analog5),
        analog6: updateOffer(activeOffers.analog6),
      };
      setActiveOffers(newOffers);

      let newLocalDeliveryPrice = localDeliveryPrice;
      let newLocalSkidkaD = localSkidkaD;

      if (originalDelivery && originalDelivery.name !== 'Самовывоз' && localSkidkaD !== null) {
        const originalDeliveryPrice = Math.round(parseFloat(originalDelivery.costPerTrip) || 0);
        const parsedSkidkaD = localSkidkaD === '' ? '' : Math.round(parseFloat(localSkidkaD) || 0);
        newLocalDeliveryPrice = calculatePriceFromSkidkaOrMarzha(originalDeliveryPrice, parsedSkidkaD, newId === 2);
        newLocalSkidkaD = calculateSkidkaOrMarzha(newLocalDeliveryPrice, originalDeliveryPrice, newId === 2) || '';
      }

      setLocalDeliveryPrice(newLocalDeliveryPrice || 0);
      setLocalSkidkaD(newLocalSkidkaD === 0 ? '' : newLocalSkidkaD);

      return { id: newId, text: newText };
    });
  };

  const handleDeliveryPriceChange = (newPrice, offerType) => {
    const offer = activeOffers[offerType];
    // Берем исходную цену доставки из originalDelivery
    const originalPrice = offerType === 'original'
      ? Math.round(parseFloat(originalDelivery?.costPerTrip) || 0)
      : Math.round(parseFloat(offer.delivery?.costPerTrip) || 0);
    const parsedPrice = Math.round(parseFloat(newPrice) || 0);
    const newSkidka = calculateSkidkaOrMarzha(parsedPrice, originalPrice, per.id === 2);

    setActiveOffers((prev) => ({
      ...prev,
      [offerType]: {
        ...offer,
        delivery: offer.delivery ? {
          ...offer.delivery,
          costPerTrip: parsedPrice,
          totalCost: Math.ceil(parsedPrice * offer.delivery.numberOfTrips),
        } : null,
      },
    }));
    setLocalDeliveryPrice(offerType === 'original' ? parsedPrice : localDeliveryPrice);
    setLocalSkidkaD(offerType === 'original' ? (newSkidka === 0 ? '' : newSkidka) : localSkidkaD);
  };

  // Новая функция для полного обновления доставки
  const handleDeliveryUpdate = (newDelivery, offerType = 'original') => {
    setActiveOffers((prev) => ({
      ...prev,
      [offerType]: {
        ...prev[offerType],
        delivery: {
          ...newDelivery,
        },
      },
    }));

    // Обновляем originalDelivery если это основное предложение
    if (offerType === 'original') {
      setOriginalDelivery(newDelivery);
      setLocalDeliveryPrice(newDelivery.costPerTrip);
      setLocalSkidkaD(''); // Сбрасываем скидку при выборе новой доставки
      if (setSelectedOffer && isGazOffer(activeOffers?.original?.materials || [])) {
        setSelectedOffer((prev) => ({
          ...prev,
          delivery: newDelivery,
        }));
      }
    }
  };

  // Новая функция для изменения метода доставки глобально
  const handleDeliveryMethodChange = async (newDeliveryMethod, offerType = 'original') => {
    console.log("🔄 handleDeliveryMethodChange вызван с:", { newDeliveryMethod, offerType });
    if (!setDeliveryMethod || !setSelectedOffer) {
      console.log("❌ setDeliveryMethod или setSelectedOffer не переданы");
      return;
    }

    // Определяем категорию для обновления deliveryMethod
    const materialCategory = selectedOffer?.materials?.[0]?.calculator;
    if (!materialCategory) {
      console.log("❌ materialCategory не найдена");
      return;
    }

    const categoryKey = getCategoryForMaterial({ calculator: materialCategory });
    console.log("🔄 Определена категория:", { materialCategory, categoryKey });

    // Обновляем глобальный deliveryMethod
    setDeliveryMethod(prev => {
      const newState = {
        ...prev,
        [categoryKey]: newDeliveryMethod
      };
      console.log("🔄 Обновляем deliveryMethod:", { prev, newState });
      return newState;
    });

    // Проверяем, является ли это оптимальным маршрутом
    const isOptimalRoute = selectedOffer?.production === "Оптимальный маршрут";

    // Пересчитываем доставку для текущего offer
    if (offerType === 'original' && activeOffers.original && newDeliveryMethod !== 'Самовывоз') {
      if (isOptimalRoute) {
        // Для оптимального маршрута используем уже рассчитанные deliveryMethods
        console.log("🚚 Обновляем доставку для оптимального маршрута из deliveryMethods");
        const deliveryMethods = selectedOffer?.deliveryMethods || [];
        const selectedMethod = deliveryMethods.find(method => method.name === newDeliveryMethod);

        if (selectedMethod) {
          const updatedDelivery = {
            name: selectedMethod.name,
            numberOfTrips: selectedMethod.numberOfTrips,
            costPerTrip: selectedMethod.costPerTrip,
            totalCost: selectedMethod.totalCost,
            bitrixId: selectedMethod.bitrixId || "",
          };

          console.log("✅ Найден метод доставки для оптимального маршрута:", updatedDelivery);

          // Обновляем originalDelivery
          setOriginalDelivery(updatedDelivery);
          setLocalDeliveryPrice(Math.round(parseFloat(updatedDelivery.costPerTrip) || 0));

          // Обновляем delivery в activeOffers.original
          setActiveOffers(prev => ({
            ...prev,
            original: {
              ...prev.original,
              delivery: updatedDelivery
            }
          }));

          // Для сохранения старого поведения других категорий синхронизируем selectedOffer только для газобетона
          if (isGazOffer(activeOffers?.original?.materials || [])) {
            setSelectedOffer(prev => ({
              ...prev,
              delivery: updatedDelivery
            }));
          }

          console.log("✅ Доставка обновлена для оптимального маршрута");
        } else {
          console.log("❌ Метод доставки не найден в deliveryMethods оптимального маршрута");
        }
      } else {
        // Для обычных предложений делаем API запрос
        console.log("🚚 Пересчитываем доставку для обычного предложения");
        const updatedDelivery = await recalculateDelivery(
          activeOffers.original.materials,
          getDeliveryRecalculationInput(activeOffers.original.materials, newDeliveryMethod),
          offerRegion
        );
        if (updatedDelivery) {
          console.log("✅ Новая доставка рассчитана:", updatedDelivery);

          // Обновляем originalDelivery
          setOriginalDelivery(updatedDelivery);
          setLocalDeliveryPrice(Math.round(parseFloat(updatedDelivery.costPerTrip) || 0));

          // Обновляем delivery в activeOffers.original
          setActiveOffers(prev => ({
            ...prev,
            original: {
              ...prev.original,
              delivery: updatedDelivery
            }
          }));

          // Для сохранения старого поведения других категорий синхронизируем selectedOffer только для газобетона
          if (isGazOffer(activeOffers?.original?.materials || [])) {
            setSelectedOffer(prev => ({
              ...prev,
              delivery: updatedDelivery
            }));
          }

          console.log("✅ Доставка обновлена в offer и selectedOffer");
        } else {
          console.log("❌ Не удалось пересчитать доставку");
        }
      }
    }

    // Пересчет в Productions произойдет автоматически при изменении deliveryMethod
    // setSelectedOffer(null) НЕ нужен и приводит к закрытию Offer блока

    console.log("🔄 Обновлен метод доставки:", { categoryKey, newDeliveryMethod });
  };

  const handleSkidkaChangeDelivery = (skidka, offerType) => {
    const offer = activeOffers[offerType];
    // Берем исходную цену доставки из originalDelivery
    const originalPrice = offerType === 'original'
      ? Math.round(parseFloat(originalDelivery?.costPerTrip) || 0)
      : Math.round(parseFloat(offer.delivery?.costPerTrip) || 0);
    const parsedSkidka = skidka === '' ? '' : Math.round(parseFloat(skidka) || 0);
    const newPrice = calculatePriceFromSkidkaOrMarzha(originalPrice, parsedSkidka, per.id === 2);

    setActiveOffers((prev) => ({
      ...prev,
      [offerType]: {
        ...offer,
        delivery: offer.delivery ? {
          ...offer.delivery,
          costPerTrip: newPrice,
          totalCost: Math.ceil(newPrice * offer.delivery.numberOfTrips),
        } : null,
      },
    }));
    setLocalSkidkaD(offerType === 'original' ? parsedSkidka : localSkidkaD);
    setLocalDeliveryPrice(offerType === 'original' ? newPrice : localDeliveryPrice);
  };

  const handleButtonClick = async (scenario) => {
    setButtonStates((prev) => ({
      ...prev,
      [scenario]: !prev[scenario],
    }));

    if (!buttonStates[scenario]) {
      if (!analogs.length) {
        setResponseText('Аналоги не найдены');
        return;
      }

      if (scenario === 1) {
        setResponseText(`У вас есть цена дешевле?`);
        setActiveOffers((prev) => ({
          ...prev,
        }));
      } else if (scenario === 8) {
        setResponseText('Мы предложили все, что было в наших силах и по вашему запросу он подходит');
        setActiveOffers((prev) => ({
          ...prev,
        }));
      } else if (scenario >= 2 && scenario <= 7) {
        const analogIndex = scenario - 1;
        if (analogs.length > analogIndex) {
          const analog = analogs[analogIndex];
          const newMaterial = {
            ...originalMaterials[0],
            name: analog.value,
            pricePerOne: Math.ceil(analog.pricePerPackage),
            sum: Math.ceil(analog.pricePerPackage * analog.requiredPackages),
            manufacturer: analog.details.manufacturer,
            volumePerOne: analog.volumePerOne,
            massPerOne: analog.massPerOne,
            unit: analog.details.unit || originalMaterials[0].unit,
            quantity: analog.requiredPackages,
          };
          const newDelivery = analogDeliveries[`analog${scenario - 1}`] || (
            originalDelivery.name !== 'Самовывоз'
              ? await recalculateDelivery(
                [newMaterial],
                getDeliveryRecalculationInput([newMaterial], originalDelivery.name),
                offerRegion
              )
              : null
          );
          setActiveOffers((prev) => ({
            ...prev,
            [`analog${scenario - 1}`]: { materials: [newMaterial], delivery: newDelivery, type: `analog${scenario - 1}` },
          }));
        }
      }
    } else {
      if (scenario >= 2 && scenario <= 7) {
        setActiveOffers((prev) => ({
          ...prev,
          [`analog${scenario - 1}`]: null,
        }));
      }
    }
  };

  // prebuiltProducts — готовый массив товарных строк (для газобетонного блочного КП)
  const handleFillBitrix = async (offer, prebuiltProducts) => {
    try {
      let productsToSend;

      if (Array.isArray(prebuiltProducts) && prebuiltProducts.length > 0) {
        productsToSend = prebuiltProducts;
      } else {
        const isOriginal = offer.type === 'original';
        productsToSend = offer.materials.map((material, index) => {
          const localPriceIndex = material.__originalIndex ?? index;
          return {
            bitrixId: material.bitrixId || material.name,
            price: isOriginal ? parseFloat(localPrices[localPriceIndex]) || 0 : parseFloat(material.pricePerOne) || 0,
            quantity: parseFloat(material.quantity) || 1,
            measureCode: material.measureCode || '796',
          };
        });
        if (offer.delivery && offer.delivery.name !== 'Самовывоз') {
          productsToSend.push({
            bitrixId: offer.delivery.bitrixId,
            price: isOriginal ? parseFloat(localDeliveryPrice) || 0 : parseFloat(offer.delivery.costPerTrip) || 0,
            quantity: parseFloat(offer.delivery.numberOfTrips) || 1,
            measureCode: offer.delivery.measureCode || '796',
          });
        }
      }

      if (!dealId) {
        throw new Error('ID сделки не найден');
      }

      console.log('Отправка в Bitrix:', { dealId, products: productsToSend });

      const response = await fetch(`${PATH_TO_BACKEND}/api/sender`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dealId: dealId,
          products: productsToSend,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Ошибка при отправке в Bitrix');
      }

      setNotification({
        type: 'success',
        message: "Товары в сделке заполнены 🙂.\nДля отображения товаров - обновите страницу сделки 🔄",
      });

      setTimeout(() => setNotification(null), 5000);
    } catch (error) {
      console.error('Ошибка при заполнении сделки в Bitrix:', error);
      setNotification({
        type: 'error',
        message: `Ошибка: ${error.message}`,
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const getCurrentOriginalDeliveryName = () => {
    return activeOffers?.original?.delivery?.name || originalDelivery?.name || '';
  };

  // Пакетное добавление — только для окна «Дополнить машину» в МСК Газобетоне.
  // Отдельная функция, а не общий путь: добавлять отмеченные позиции по одной нельзя,
  // потому что каждый вызов читает состав заказа на момент вызова и все позиции, кроме
  // последней, потерялись бы. Общий путь одиночного добавления живёт ниже отдельно и
  // остаётся базовым.
  //
  // Категория для выбора метода доставки берётся у первой позиции. Это безопасно
  // именно здесь: окно отдаёт только допродажи газобетонной строки, а они отбираются
  // по product_group = «Газобетон и кирпич» (U-блок, клей, добавка противоморозная),
  // поэтому у всех позиций пакета категория одна и та же — «gaz». Ответ номенклатуры
  // поля calculator не содержит, подменить product_group нечему.
  const addMaterialsFromTooltip = async (newMaterials) => {
    const materialsToAdd = (Array.isArray(newMaterials) ? newMaterials : [newMaterials]).filter(Boolean);
    if (materialsToAdd.length === 0) {
      return;
    }
    const newMaterial = materialsToAdd[0];
    console.log('Adding materials from upsell modal:', materialsToAdd);
    const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0;

    const baseMaterials = activeOffers?.original?.materials?.length
      ? activeOffers.original.materials
      : originalMaterials;
    const allMaterials = [...baseMaterials, ...materialsToAdd];

    // Синхронизируем локальное состояние состава сразу после добавления
    setAdditionalMaterials(prev => [...prev, ...materialsToAdd]);
    setOriginalMaterials(allMaterials);
    setLocalPrices(prev => [
      ...prev,
      ...materialsToAdd.map((material) => roundPriceForMaterial(material.pricePerOne, material)),
    ]);
    setLocalSkidka(prev => [...prev, ...materialsToAdd.map(() => '')]);

    if (setSelectedOffer) {
      setSelectedOffer((prev) => ({
        ...prev,
        materials: allMaterials,
      }));
    }

    const currentDeliveryName = getCurrentOriginalDeliveryName();
    if (currentDeliveryName && currentDeliveryName !== 'Самовывоз') {
      const category = getCategoryForMaterial(newMaterial);
      const deliveryMethodForCategory = category === 'gaz'
        ? (currentDeliveryName || deliveryMethod?.[category])
        : (deliveryMethod?.[category] || currentDeliveryName);
      console.log('Категория товара:', category);
      console.log('Метод доставки для категории:', deliveryMethodForCategory);

      const updatedDelivery = await recalculateDelivery(
        allMaterials,
        getDeliveryRecalculationInput(allMaterials, deliveryMethodForCategory),
        offerRegion
      );
      if (updatedDelivery) {
        setOriginalDelivery(updatedDelivery);
        setLocalDeliveryPrice(Math.round(parseFloat(updatedDelivery.costPerTrip) || 0));
        setActiveOffers((prev) => ({
          ...prev,
          original: {
            ...prev.original,
            materials: allMaterials,
            delivery: updatedDelivery,
          },
        }));
        if (setSelectedOffer && isGazOffer(allMaterials)) {
          setSelectedOffer((prev) => ({
            ...prev,
            materials: allMaterials,
            delivery: updatedDelivery,
          }));
        }
        restoreWindowScrollPosition(currentScrollY);
      } else {
        console.warn('Не удалось рассчитать доставку для новых материалов');
        setActiveOffers((prev) => ({
          ...prev,
          original: {
            ...prev.original,
            materials: allMaterials,
          },
        }));
        restoreWindowScrollPosition(currentScrollY);
      }
      return;
    }

    // Для самовывоза просто обновляем материалы
    setActiveOffers((prev) => ({
      ...prev,
      original: {
        ...prev.original,
        materials: allMaterials,
      },
    }));
    restoreWindowScrollPosition(currentScrollY);
  };

  // Изменение количества уже добавленной допродажи. Меняет только позицию, которая
  // была добавлена из каталога (isFromInsulationFriends), и пересчитывает доставку
  // тем же путём, что добавление и удаление. Своей арифметики по цене нет:
  // сумма = цена позиции × количество, как и при добавлении.
  const updateAdditionalMaterialQuantity = async (materialIndex, nextQuantity) => {
    const quantity = Number(nextQuantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      return;
    }

    const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
    const currentMaterials = activeOffers?.original?.materials?.length
      ? activeOffers.original.materials
      : originalMaterials;
    const material = currentMaterials[materialIndex];

    if (!material?.isFromInsulationFriends) {
      console.warn('Количество можно менять только у добавленной позиции');
      return;
    }

    const pricePerOne = Number(material.pricePerOne) || 0;
    const volumePerOne = Number(material.volumePerOne) || 0;
    const palletUnits = Number(material.palletUnits) || 0;
    const updatedMaterial = {
      ...material,
      quantity,
      sum: pricePerOne * quantity,
      ...(volumePerOne > 0 ? { factVolume: Math.round(quantity * volumePerOne * 1000) / 1000 } : {}),
      ...(palletUnits > 0 ? { numberPallets: Math.ceil(quantity / palletUnits) } : {}),
    };

    const newMaterials = currentMaterials.map((item, index) => (
      index === materialIndex ? updatedMaterial : item
    ));

    setAdditionalMaterials(newMaterials.filter((item) => item?.isFromInsulationFriends));
    setOriginalMaterials(newMaterials);

    if (setSelectedOffer) {
      setSelectedOffer((prev) => ({ ...prev, materials: newMaterials }));
    }

    const currentDeliveryName = getCurrentOriginalDeliveryName();
    if (currentDeliveryName && currentDeliveryName !== 'Самовывоз') {
      const updatedDelivery = await recalculateDelivery(
        newMaterials,
        getDeliveryRecalculationInput(newMaterials, currentDeliveryName),
        offerRegion
      );
      if (updatedDelivery) {
        setOriginalDelivery(updatedDelivery);
        setLocalDeliveryPrice(Math.round(parseFloat(updatedDelivery.costPerTrip) || 0));
        setActiveOffers((prev) => ({
          ...prev,
          original: { ...prev.original, materials: newMaterials, delivery: updatedDelivery },
        }));
        restoreWindowScrollPosition(currentScrollY);
        return;
      }
    }

    setActiveOffers((prev) => ({
      ...prev,
      original: { ...prev.original, materials: newMaterials },
    }));
    restoreWindowScrollPosition(currentScrollY);
  };

  // Общий путь добавления одной позиции. Дословно как в 5442898: им пользуются все
  // калькуляторы, поэтому он намеренно не выражен через пакетную функцию — обёртка
  // меняла бы поведение на falsy-аргументе и связывала бы общий путь с газобетонным.
  // Совпадение с базой проверяется тестом tests/gazobeton/add_material_paths.test.js.
  const addMaterialFromTooltip = async (newMaterial) => {
    console.log('Adding material from tooltip:', newMaterial);
    const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0;

    const baseMaterials = activeOffers?.original?.materials?.length
      ? activeOffers.original.materials
      : originalMaterials;
    const allMaterials = [...baseMaterials, newMaterial];

    // Синхронизируем локальное состояние состава сразу после добавления
    setAdditionalMaterials(prev => [...prev, newMaterial]);
    setOriginalMaterials(allMaterials);
    setLocalPrices(prev => [...prev, roundPriceForMaterial(newMaterial.pricePerOne, newMaterial)]);
    setLocalSkidka(prev => [...prev, '']);

    if (setSelectedOffer) {
      setSelectedOffer((prev) => ({
        ...prev,
        materials: allMaterials,
      }));
    }

    const currentDeliveryName = getCurrentOriginalDeliveryName();
    if (currentDeliveryName && currentDeliveryName !== 'Самовывоз') {
      const category = getCategoryForMaterial(newMaterial);
      const deliveryMethodForCategory = category === 'gaz'
        ? (currentDeliveryName || deliveryMethod?.[category])
        : (deliveryMethod?.[category] || currentDeliveryName);
      console.log('Категория товара:', category);
      console.log('Метод доставки для категории:', deliveryMethodForCategory);

      const updatedDelivery = await recalculateDelivery(
        allMaterials,
        getDeliveryRecalculationInput(allMaterials, deliveryMethodForCategory),
        offerRegion
      );
      if (updatedDelivery) {
        setOriginalDelivery(updatedDelivery);
        setLocalDeliveryPrice(Math.round(parseFloat(updatedDelivery.costPerTrip) || 0));
        setActiveOffers((prev) => ({
          ...prev,
          original: {
            ...prev.original,
            materials: allMaterials,
            delivery: updatedDelivery,
          },
        }));
        if (setSelectedOffer && isGazOffer(allMaterials)) {
          setSelectedOffer((prev) => ({
            ...prev,
            materials: allMaterials,
            delivery: updatedDelivery,
          }));
        }
        restoreWindowScrollPosition(currentScrollY);
      } else {
        console.warn('Не удалось рассчитать доставку для новых материалов');
        setActiveOffers((prev) => ({
          ...prev,
          original: {
            ...prev.original,
            materials: allMaterials,
          },
        }));
        restoreWindowScrollPosition(currentScrollY);
      }
      return;
    }

    // Для самовывоза просто обновляем материалы
    setActiveOffers((prev) => ({
      ...prev,
      original: {
        ...prev.original,
        materials: allMaterials,
      },
    }));
    restoreWindowScrollPosition(currentScrollY);
  };

  const removeMaterial = async (materialIndex) => {
    console.log('Removing material at index:', materialIndex);
    const currentScrollY = typeof window !== 'undefined' ? window.scrollY : 0;

    const currentMaterials = activeOffers?.original?.materials?.length
      ? activeOffers.original.materials
      : originalMaterials;
    const materialToRemove = currentMaterials[materialIndex];

    if (!materialToRemove?.isFromInsulationFriends) {
      console.warn('Cannot remove non-additional material');
      return;
    }

    const newMaterials = currentMaterials.filter((_, index) => index !== materialIndex);
    const newAdditionalMaterials = newMaterials.filter((material) => material?.isFromInsulationFriends);
    setAdditionalMaterials(newAdditionalMaterials);
    setOriginalMaterials(newMaterials);

    setLocalPrices(prev => prev.filter((_, index) => index !== materialIndex));
    setLocalSkidka(prev => prev.filter((_, index) => index !== materialIndex));

    if (setSelectedOffer) {
      setSelectedOffer((prev) => ({
        ...prev,
        materials: newMaterials,
      }));
    }

    const currentDeliveryName = getCurrentOriginalDeliveryName();
    if (currentDeliveryName && currentDeliveryName !== 'Самовывоз') {
      const updatedDelivery = await recalculateDelivery(
        newMaterials,
        getDeliveryRecalculationInput(newMaterials, currentDeliveryName),
        offerRegion
      );
      if (updatedDelivery) {
        setOriginalDelivery(updatedDelivery);
        setLocalDeliveryPrice(Math.round(parseFloat(updatedDelivery.costPerTrip) || 0));

        // Обновляем activeOffers
        setActiveOffers((prev) => ({
          ...prev,
          original: {
            ...prev.original,
            materials: newMaterials,
            delivery: updatedDelivery
          },
        }));
        if (setSelectedOffer && isGazOffer(newMaterials)) {
          setSelectedOffer((prev) => ({
            ...prev,
            materials: newMaterials,
            delivery: updatedDelivery,
          }));
        }
        restoreWindowScrollPosition(currentScrollY);
      } else {
        // Обновляем только материалы
        setActiveOffers((prev) => ({
          ...prev,
          original: {
            ...prev.original,
            materials: newMaterials
          },
        }));
        restoreWindowScrollPosition(currentScrollY);
      }
    } else {
      // Для самовывоза просто обновляем материалы
      setActiveOffers((prev) => ({
        ...prev,
        original: {
          ...prev.original,
          materials: newMaterials
        },
      }));
      restoreWindowScrollPosition(currentScrollY);
    }
  };

  const buildUnderfilledShalandaReduction = useCallback(async (palletsToRemove, offerType = 'original', targetMaterialIndex = null) => {
    const offer = activeOffers?.[offerType];
    const materials = Array.isArray(offer?.materials) ? offer.materials : [];
    if (!materials.length) {
      return null;
    }

    const gazMaterialIndexes = [...materials]
      .map((material, index) => ({ material, index }))
      .filter(({ material }) => isGazMaterial(material))
      .map(({ index }) => index)
      ;

    const fallbackGazMaterialIndex = gazMaterialIndexes.at(-1);
    const selectedMaterialIndex = Number.isInteger(targetMaterialIndex) ? targetMaterialIndex : fallbackGazMaterialIndex;

    if (!Number.isInteger(selectedMaterialIndex) || !gazMaterialIndexes.includes(selectedMaterialIndex)) {
      return null;
    }

    const palletsCount = parsePositiveNumber(palletsToRemove);
    const targetMaterial = materials[selectedMaterialIndex];
    const palletUnits = parsePositiveNumber(targetMaterial?.palletUnits);
    const currentQuantity = parsePositiveNumber(targetMaterial?.quantity);

    if (!palletsCount || !palletUnits || !currentQuantity) {
      return null;
    }

    const quantityToRemove = palletsCount * palletUnits;
    const nextQuantityRaw = Math.max(currentQuantity - quantityToRemove, 0);
    const shouldRemoveMaterial = nextQuantityRaw <= 0;

    const updatedMaterials = materials.flatMap((material, index) => {
      if (index !== selectedMaterialIndex) {
        return [material];
      }

      if (shouldRemoveMaterial) {
        return [];
      }

      const nextQuantity = Number(nextQuantityRaw.toFixed(3));
      const nextNumberPallets = Math.max(Math.ceil(nextQuantity / palletUnits), 0);

      return [{
        ...material,
        quantity: nextQuantity,
        sum: roundSumForMaterial((Number(material.pricePerOne) || 0) * nextQuantity, material),
        factVolume: nextQuantity,
        numberPallets: nextNumberPallets,
        occupiedPallets: nextNumberPallets,
      }];
    });

    const currentDeliveryName = offer?.delivery?.name || getCurrentOriginalDeliveryName();
    let updatedDelivery = offer?.delivery || null;

    if (!updatedMaterials.length) {
      updatedDelivery = null;
    } else if (currentDeliveryName && currentDeliveryName !== 'Самовывоз') {
      updatedDelivery = await recalculateDelivery(
        updatedMaterials,
        getDeliveryRecalculationInput(updatedMaterials, currentDeliveryName),
        offerRegion
      );

      if (!updatedDelivery) {
        return null;
      }
    }

    const materialTotal = updatedMaterials.reduce((sum, material) => (
      sum + roundSumForMaterial((Number(material.pricePerOne) || 0) * (Number(material.quantity) || 0), material)
    ), 0);
    const deliveryTotal = updatedDelivery?.totalCost || 0;

    return {
      updatedMaterials,
      updatedDelivery,
      removedPallets: palletsCount,
      quantityToRemove,
      reducedMaterialIndex: selectedMaterialIndex,
      removedMaterial: shouldRemoveMaterial,
      materialTotal,
      deliveryTotal,
      totalOrderCost: materialTotal + deliveryTotal,
    };
  }, [activeOffers, recalculateDelivery, deliveryMethod]);

  const applyUnderfilledShalandaReduction = useCallback(async (palletsToRemove, offerType = 'original', targetMaterialIndex = null) => {
    const reduction = await buildUnderfilledShalandaReduction(palletsToRemove, offerType, targetMaterialIndex);
    if (!reduction) {
      return false;
    }

    const { updatedMaterials, updatedDelivery, reducedMaterialIndex, removedMaterial } = reduction;

    setOriginalMaterials(updatedMaterials);

    setLocalPrices((prev) => {
      if (removedMaterial) {
        return prev.filter((_, index) => index !== reducedMaterialIndex);
      }

      return prev.map((value, index) => {
        if (index !== reducedMaterialIndex) {
          return value;
        }

        return roundPriceForMaterial(updatedMaterials[index]?.pricePerOne, updatedMaterials[index]);
      });
    });

    setLocalSkidka((prev) => {
      if (removedMaterial) {
        return prev.filter((_, index) => index !== reducedMaterialIndex);
      }
      return prev;
    });

    setActiveOffers((prev) => ({
      ...prev,
      [offerType]: {
        ...prev[offerType],
        materials: updatedMaterials,
        delivery: updatedDelivery,
      },
    }));

    if (offerType === 'original') {
      setOriginalDelivery(updatedDelivery);
      setLocalDeliveryPrice(Math.round(parseFloat(updatedDelivery?.costPerTrip) || 0));
      setLocalSkidkaD('');
    }

    if (setSelectedOffer) {
      setSelectedOffer((prev) => ({
        ...prev,
        materials: updatedMaterials,
        delivery: updatedDelivery,
      }));
    }

    return true;
  }, [buildUnderfilledShalandaReduction, setSelectedOffer]);

  return {
    activeOffers,
    localPrices,
    localDeliveryPrice,
    localSkidka,
    localSkidkaD,
    localGlobalSkidka,
    per,
    analogs,
    analogDeliveries,
    buttonStates,
    notification,
    setNotification,
    tooltip,
    responseText,
    setTooltip,
    isLoadingAnalogs,
    handleMaterialPriceChange,
    handleSkidkaChange,
    handleDeliveryPriceChange,
    handleDeliveryUpdate,
    handleDeliveryMethodChange,
    handleSkidkaChangeDelivery,
    handleGlobalSkidkaChange,
    skidkaOrMarzha,
    handleButtonClick,
    handleFillBitrix,
    addMaterialFromTooltip,
    addMaterialsFromTooltip,
    updateAdditionalMaterialQuantity,
    removeMaterial,
    buildUnderfilledShalandaReduction,
    applyUnderfilledShalandaReduction,
  };
};
